const blockedPublicVenueDomains = new Set([
  "activeactivities.com.au",
  "badmintonact.asn.au",
  "badmintonact.org.au",
  "badmintoncourt.au",
  "badmintonnsw.org.au",
  "badmintonsouthaustralia.com.au",
  "badmintontasmania.com.au",
  "badmintonvic.com.au",
  "badmintonwa.org.au",
  "demo.yepbooking.com.au",
  "keepactive.com.au",
  "npa.opdsdemo.com.au",
  "ntbadminton.org.au",
  "openstreetmap.org",
  "qba.net.au",
  "revolutionise.com.au"
]);

const blockedPublicVenueUrlTerms = [
  "/assets/assets/dummy",
  "/dummy.",
  "wait.gif",
  "facebook.com/keepactiveaustralia",
  "facebook.com/groups/214501510745111",
  "instagram.com/keepactiveaustralia",
  "youtube.com/@keepactiveaustralia",
  "linkedin.com/company/keep-active-australia",
  "facebook.com/activeactivities",
  "twitter.com/activeactivity",
  "x.com/activeactivity"
];

const directorySourcePattern = /\b(keepactive|activeactivities)\b/i;
const technicalDescriptionPattern = /\b(overture maps|openstreetmap|geonames|mapped public facts|public venue data|source dataset|source licen[cs]e|badminta stores factual|no imported image|keepactive|activeactivities)\b/i;
const internalDetailLabelPattern = /^(geo lookup|overture category|mapped city seed|mapped country|source dataset|source licence|source license|data licence|data license|raw source|taxonomy|import note)$/i;
const internalDetailSourcePattern = /\b(overture maps|geonames|keepactive|activeactivities|coordinate lookup|import pipeline)\b/i;
const unrelatedSportPattern = /\b(soccer|football|basketball|netball|pickleball|baseball|golf|swimming|swim school|gymnastics|dance classes|martial arts|ice skating|hockey)\b/i;
const badmintonPattern = /(\bbadminton\b|\bshuttle(?:cock)?\b|\u7fbd\u6bdb\u7403|\u7fbd\u7403|\ubc30\ub4dc\ubbfc\ud134|\u30d0\u30c9\u30df\u30f3\u30c8\u30f3)/i;
const technicalAmenityPattern = /\b(overture category|source dataset|source licence|source license|mapped city|mapped country|no imported images?|media status|image rights|keepactive|activeactivities|club finder)\b/i;

const normaliseSources = (row = {}) => (Array.isArray(row.sources) ? row.sources : [row.source])
  .map((source) => String(source || "").trim())
  .filter(Boolean);

export const getVenueSourceCategory = (row = {}) => {
  const direct = String(row.source_category || row.sourceCategory || "").trim();
  if (direct) return direct.toLowerCase();
  const details = Array.isArray(row.detail_items) ? row.detail_items : Array.isArray(row.detailItems) ? row.detailItems : [];
  const categoryItem = details.find((item) => /^overture category$/i.test(String(item?.label || "").trim()));
  return String(categoryItem?.value || "").trim().toLowerCase();
};

export const isExplicitTestVenueRecord = (row = {}) => /^(test|demo)(\s|$)|\btest club\b/i.test(String(row.name || row.title || "").trim());

export const isBlockedPublicVenueUrl = (url = "") => {
  const value = String(url || "").trim();
  if (!value) return false;
  try {
    const parsed = new URL(value);
    if (!/^https?:$/.test(parsed.protocol) || !parsed.hostname) return true;
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    const href = parsed.href.toLowerCase();
    return blockedPublicVenueDomains.has(host)
      || [...blockedPublicVenueDomains].some((domain) => host.endsWith(`.${domain}`))
      || host.startsWith("demo.")
      || host.includes(".demo.")
      || host.includes("opdsdemo")
      || blockedPublicVenueUrlTerms.some((term) => href.includes(term));
  } catch {
    return true;
  }
};

export const sanitisePublicVenueLinks = (links = []) => (Array.isArray(links) ? links : [])
  .map((link) => {
    const url = String(typeof link === "string" ? link : link?.url || "").trim();
    if (!url || isBlockedPublicVenueUrl(url)) return null;
    try {
      const parsed = new URL(url);
      if (!/^https?:$/.test(parsed.protocol)) return null;
    } catch {
      return null;
    }
    const label = String(typeof link === "string" ? "" : link?.label || "").trim();
    if (/\b(demo|directory|club finder)\b/i.test(label)) return null;
    return typeof link === "string" ? { label: "", url } : { ...link, label, url };
  })
  .filter(Boolean);

export const sanitisePublicVenueDescription = (row = {}) => {
  const description = String(row.description || "").replace(/\s+/g, " ").trim();
  if (!description) return "";
  const sources = normaliseSources(row).join(" ");
  const ownerManaged = row.ownerCreated === true
    || row.owner_created === true
    || Boolean(row.ownerUid || row.owner_uid)
    || ["claimed", "approved", "verified"].includes(String(row.claimStatus || row.claim_status || "").trim().toLowerCase());
  if ((!ownerManaged && directorySourcePattern.test(sources)) || technicalDescriptionPattern.test(description)) return "";
  if (unrelatedSportPattern.test(description) && !badmintonPattern.test(description)) return "";
  return description.slice(0, 1600);
};

export const sanitisePublicVenueDetailItems = (items = []) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).reduce((safeItems, item) => {
    const label = String(item?.label || "").replace(/\s+/g, " ").trim();
    const value = String(item?.value || "").replace(/\s+/g, " ").trim();
    const source = String(item?.source || "").replace(/\s+/g, " ").trim();
    if (!label || !value) return safeItems;
    if (internalDetailLabelPattern.test(label) || internalDetailSourcePattern.test(source)) return safeItems;
    if (technicalDescriptionPattern.test(value)) return safeItems;
    if (unrelatedSportPattern.test(value) && !badmintonPattern.test(value)) return safeItems;
    const key = `${label}|${value}`.toLowerCase();
    if (seen.has(key)) return safeItems;
    seen.add(key);
    safeItems.push({ ...item, label: label.slice(0, 100), value: value.slice(0, 600), source: source.slice(0, 120) });
    return safeItems;
  }, []).slice(0, 20);
};

const friendlyAmenity = (amenity = "") => {
  const value = String(amenity || "").replace(/\s+/g, " ").trim();
  if (!value || technicalAmenityPattern.test(value)) return "";
  if (/^sport\s*:\s*badminton$/i.test(value)) return "Badminton courts";
  if (/^sport\s*:\s*.*badminton/i.test(value)) return "Badminton and multi-sport courts";
  if (/^leisure\s*:\s*sports?_centre$/i.test(value)) return "Sports centre";
  if (/^leisure\s*:\s*pitch$/i.test(value)) return "Sports venue";
  if (/^indoor\s*:\s*yes$/i.test(value)) return "Indoor courts";
  if (/^lit\s*:\s*yes$/i.test(value)) return "Court lighting";
  if (/^surface\s*:\s*wood$/i.test(value)) return "Wood court surface";
  if (/^[a-z_ -]+\s*:\s*/i.test(value)) return "";
  return value.slice(0, 120);
};

export const sanitisePublicVenueAmenities = (amenities = []) => {
  const seen = new Set();
  return (Array.isArray(amenities) ? amenities : [])
    .map(friendlyAmenity)
    .filter((amenity) => {
      const key = amenity.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 30);
};

export const sanitisePublicVenueRow = (row = {}) => ({
  ...row,
  source_category: getVenueSourceCategory(row),
  description: sanitisePublicVenueDescription(row),
  amenities: sanitisePublicVenueAmenities(row.amenities),
  detail_items: sanitisePublicVenueDetailItems(row.detail_items),
  social_links: sanitisePublicVenueLinks(row.social_links),
  booking_links: sanitisePublicVenueLinks(row.booking_links),
  source_urls: sanitisePublicVenueLinks(row.source_urls).map((link) => link.url),
  website: isBlockedPublicVenueUrl(row.website) ? "" : String(row.website || "").trim()
});
